import React, { useEffect } from 'react'
import { useStore } from 'zustand'
import { createDocumentsStore } from './features/documents/documents-store'
import type {
  DocumentsStore,
  DocumentsStoreApi,
} from './features/documents/documents-store'
import type { ApolloClient } from '@apollo/client'
import type { DocumentRecordClient } from './document-record-client'

const DocumentsStoreContext = React.createContext<DocumentsStoreApi | null>(
  null,
)

export function getContext({
  records,
  client,
}: {
  records: DocumentRecordClient
  client: ApolloClient
}) {
  return {
    documentsStore: createDocumentsStore({ records, client }),
  }
}

export function Provider({
  children,
  documentsStore,
}: {
  children: React.ReactNode
  documentsStore: DocumentsStoreApi
}) {
  return (
    <DocumentsStoreContext.Provider value={documentsStore}>
      <DocumentsStoreInitializer />
      {children}
    </DocumentsStoreContext.Provider>
  )
}

function DocumentsStoreInitializer() {
  const store = useDocumentsStoreApi()

  useEffect(() => {
    store.getState().startSync()

    return () => {
      store.getState().stopSync()
    }
  }, [store])

  return null
}

export function useDocumentsStore<T>(
  selector: (state: DocumentsStore) => T,
): T {
  const store = React.useContext(DocumentsStoreContext)

  if (!store) {
    throw new Error('useDocumentsStore must be used within ZustandProvider')
  }

  return useStore(store, selector)
}

export function useDocumentsStoreApi(): DocumentsStoreApi {
  const store = React.useContext(DocumentsStoreContext)

  if (!store) {
    throw new Error('useDocumentsStoreApi must be used within ZustandProvider')
  }

  return store
}

export function useOptionalDocumentsStoreApi(): DocumentsStoreApi | null {
  return React.useContext(DocumentsStoreContext)
}
